import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { usd } from "@/lib/format";
import { Pill } from "@/components/kit";
import { ArrowLeft, Camera, CheckCircle2, Circle, Phone, Package, ClipboardList, MapPin, Loader2 } from "lucide-react";
import FilesPanel from "@/components/FilesPanel";
import { toast } from "sonner";

const readFile = (file) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });

export default function JobDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [order, setOrder] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get(`/jobs/${id}`).then((r) => setJob(r.data));
  useEffect(() => { load(); api.get(`/jobs/${id}/material-order`).then((r) => setOrder(r.data)).catch(() => {}); }, [id]);
  if (!job) return <div className="text-navy font-display text-xl">Loading…</div>;

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { const b64 = await readFile(file); await api.post(`/jobs/${id}/photos`, { photo: b64, phase: "during" }); toast.success("Photo uploaded"); load(); }
    catch { toast.error("Upload failed"); } finally { setUploading(false); e.target.value = ""; }
  };

  const toggleMilestone = async (key) => { await api.put(`/jobs/${id}/milestone`, { key }); toast.success("Milestone confirmed"); load(); };

  return (
    <div className="max-w-2xl mx-auto">
      <button onClick={() => nav("/production")} className="flex items-center gap-1 text-sm text-slate-500 mb-3 hover:text-navy" data-testid="back-btn"><ArrowLeft className="w-4 h-4" /> Job board</button>

      <div className="rounded-2xl p-5 text-white mb-4" style={{ background: "linear-gradient(120deg,#0B2545,#0D305C)" }}>
        <div className="flex justify-between items-start">
          <div><h1 className="font-display text-3xl font-extrabold uppercase leading-none">{job.customer_name}</h1>
            <p className="text-slate-300 text-sm mt-1 flex items-center gap-1"><MapPin className="w-4 h-4" /> {job.address}</p></div>
          <Pill tone="red">{job.status?.replace("_", " ")}</Pill>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4 font-num text-center">
          <div><div className="text-lg font-bold">{usd(job.contract_amount_cents)}</div><div className="text-[10px] text-slate-400 uppercase">Contract</div></div>
          <div><div className={`text-lg font-bold ${job.gp_pct < job.target_margin_pct ? "text-amberw" : "text-white"}`}>{job.gp_pct}%</div><div className="text-[10px] text-slate-400 uppercase">GP Margin</div></div>
          <div><div className="text-lg font-bold">{job.crew || "—"}</div><div className="text-[10px] text-slate-400 uppercase">Crew</div></div>
        </div>
      </div>

      {/* GIANT photo upload (glove-friendly) */}
      <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={uploadPhoto} className="hidden" data-testid="photo-input" />
      <button data-testid="upload-photo-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
        className="w-full h-16 bg-brandred hover:bg-brandred-dark text-white font-display font-extrabold uppercase text-lg rounded-2xl border-2 border-brandred flex items-center justify-center gap-3 active:translate-y-px shadow-lg mb-3">
        {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Camera className="w-7 h-7" />} Take / Upload Job Photo
      </button>
      <a href={`tel:2145550100`} data-testid="tap-to-call" className="w-full h-14 bg-white text-navy font-bold rounded-2xl border-2 border-navy flex items-center justify-center gap-2 mb-5"><Phone className="w-5 h-5" /> Tap to Call Production Manager</a>

      {/* QC milestones */}
      <div className="mb-card p-5 mb-4">
        <h3 className="font-display font-bold uppercase text-navy mb-3 flex items-center gap-2"><ClipboardList className="w-5 h-5" /> QC Checklist</h3>
        {job.milestones?.map((m) => (
          <button key={m.key} data-testid={`milestone-${m.key}`} onClick={() => !m.done && toggleMilestone(m.key)}
            className="w-full flex items-center gap-3 py-3.5 border-b border-slate-100 last:border-0 text-left">
            {m.done ? <CheckCircle2 className="w-6 h-6 text-successg shrink-0" /> : <Circle className="w-6 h-6 text-slate-300 shrink-0" />}
            <span className={`font-semibold ${m.done ? "text-slate-400 line-through" : "text-navy"}`}>{m.label}</span>
            {m.done && <span className="ml-auto text-xs text-successg font-semibold">Confirmed</span>}
          </button>
        ))}
      </div>

      {/* Photos */}
      {job.photos?.length > 0 && (
        <div className="mb-card p-5 mb-4">
          <h3 className="font-display font-bold uppercase text-navy mb-3">Job Photos ({job.photos.length})</h3>
          <div className="grid grid-cols-3 gap-2">
            {job.photos.map((p) => <img key={p.id} src={p.url} alt="job" className="w-full h-24 object-cover rounded-lg" />)}
          </div>
        </div>
      )}

      {/* Documents */}
      <div className="mb-4">
        <h3 className="font-display font-bold uppercase text-navy mb-2 flex items-center gap-2"><ClipboardList className="w-5 h-5" /> Job Documents</h3>
        <FilesPanel jobId={id} />
      </div>

      {/* Material order */}
      {order && (
        <div className="mb-card p-5">
          <h3 className="font-display font-bold uppercase text-navy mb-2 flex items-center gap-2"><Package className="w-5 h-5" /> Material Order — {order.supplier}</h3>
          <div className="text-xs text-slate-500 mb-2">Ready to send · {order.job_address}</div>
          {order.items.map((it) => (<div key={it.sku} className="flex justify-between py-1.5 border-b border-slate-100 last:border-0 text-sm"><span className="text-navy">{it.name}</span><span className="font-num text-slate-600">{it.qty} {it.unit}</span></div>))}
        </div>
      )}
    </div>
  );
}
